import React, { FC, useMemo } from 'react';
import memoize from 'memoizee';
import { useDebounceCallback } from 'usehooks-ts';

import { NormDTO, useGetTestTypeNormsHook } from '@/api/generated';
import DropdownSelect, { DropdownSelectOption, DropdownSelectProps } from '@/components/fields/DropdownSelect';

const DEBOUNCE_DELAY = 300;

type NormsDropdownSelectProps = Omit<DropdownSelectProps, 'options'> & {
  testTypeId: string;
};

const normToOption = (norm: NormDTO): DropdownSelectOption => ({
  label: norm.title || '',
  value: norm.id || ''
});

const NormsDropdownSelect: FC<NormsDropdownSelectProps> = ({ testTypeId, ...props }) => {
  const getTestTypeNorms = useGetTestTypeNormsHook();

  const loadNormOptions = useMemo(
    () =>
      memoize(
        async (inputValue?: string) => {
          const response = await getTestTypeNorms(testTypeId, { q: inputValue });
          const newNormOptions = (response?.norms || []).map(normToOption);

          return newNormOptions;
        },
        { promise: true }
      ),
    [testTypeId, getTestTypeNorms]
  );

  const loadNormOptionsDebounced = useDebounceCallback(loadNormOptions, DEBOUNCE_DELAY, { leading: true });

  const loadNormOptionsByIds = useMemo(
    () =>
      memoize(
        async (ids?: string[]) => {
          if (!ids || ids.length === 0) return [];

          const response = await getTestTypeNorms(testTypeId);
          const normsMap = new Map((response?.norms || []).map((norm: NormDTO) => [norm.id, norm]));

          return ids
            .map(id => normsMap.get(id))
            .filter((norm): norm is NormDTO => Boolean(norm))
            .map(normToOption);
        },
        { promise: true }
      ),
    [testTypeId, getTestTypeNorms]
  );

  return (
    <DropdownSelect
      {...props}
      loadOptions={loadNormOptionsDebounced}
      loadSelectedOptions={loadNormOptionsByIds}
      isMulti={false}
      usePortal
    />
  );
};

export default NormsDropdownSelect;
